import { WIDGET_TYPES } from '@flexbyt/shared';
import { WidgetThumbnail } from '@flexbyt/widget-preview';
import { createFileRoute } from '@tanstack/react-router';
import { Jet, type JetMood } from '../components/mascot/Jet.js';
import { Button, ButtonLink, TextLink } from '../components/ui/button.js';
import { Field, SelectField } from '../components/ui/field.js';
import { Container, Measure, Rule, Section } from '../components/ui/layout.js';
import {
    Avatar,
    AvatarRow,
    RatingSummary,
    SourceBadge,
    SourceTag,
    Stars,
} from '../components/ui/review.js';
import { Badge, Card } from '../components/ui/surface.js';
import { Eyebrow, Heading, Mark, Quote, Text } from '../components/ui/type.js';
import { CONNECT_SOURCES } from '../content/sources.js';
import { seo } from '../lib/seo.js';

/**
 * The visual system on one page.
 *
 * Not a public page — `noIndex`, and nothing links to it. It exists so the
 * system can be *looked at* rather than inferred from the pages that use it:
 * every type step beside every other, every button variant on both grounds,
 * the error state of a field without having to submit a broken form.
 *
 * That is worth a route because the alternative is checking a shadow by
 * scrolling three marketing pages to find the one place it appears, and because
 * a component with no home tends to get quietly duplicated by whoever cannot
 * find it.
 */
export const Route = createFileRoute('/design-system')({
  head: () =>
    seo({
      title: 'Design system',
      description: 'The ReviewsJet marketing visual system.',
      path: '/design-system',
      noIndex: true,
    }),
  component: DesignSystem,
});

function Row({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-8">
      <h3 className="text-caption font-semibold text-ink-950">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function DesignSystem() {
  return (
    <>
      <Section size="tight">
        <Measure>
          <Eyebrow>Internal</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            The <Mark>visual system</Mark>
          </Heading>
          <Text size="lead" className="mt-5">
            Ink for structure and action, gold for stars and nothing else, blue
            for interactive text and focus. Typography and whitespace carry the
            page.
          </Text>
        </Measure>
      </Section>

      <Section size="tight" aria-label="Jet">
        <Row title="Jet — every mood">
          <div className="flex flex-wrap gap-6">
            {(['idle', 'wave', 'cheer', 'think', 'sleep', 'wow', 'sad', 'hold', 'work'] as JetMood[]).map((mood) => (
              <div key={mood} className="flex flex-col items-center gap-2 rounded-2xl bg-surface p-4">
                <Jet mood={mood} size={128} />
                <span className="text-caption text-ink-600">{mood}</span>
              </div>
            ))}
          </div>
        </Row>
      </Section>

      <Container width="page" className="pb-24">
        <Rule />

        <Row title="Type scale">
          <div className="space-y-4">
            <p className="text-display-xl text-ink-950 optical-left">Display XL</p>
            <p className="text-display-lg text-ink-950 optical-left">Display LG</p>
            <p className="text-display text-ink-950 optical-left">Display</p>
            <p className="text-display-sm text-ink-950">Display SM</p>
            <p className="text-title text-ink-950">Title</p>
            <p className="text-body-lg text-ink-600">
              Body large — the paragraph under a page heading.
            </p>
            <p className="text-body text-ink-600">
              Body — the default. Set to a 34rem measure in prose contexts,
              because a line much longer than about 68 characters stops being
              comfortable to read.
            </p>
            <p className="text-body-sm text-ink-600">Body small</p>
            <p className="text-caption text-ink-500">Caption</p>
            <p className="text-micro text-ink-500">Micro</p>
            <p className="eyebrow text-ink-400">Eyebrow</p>
          </div>
        </Row>

        <Rule />

        <Row title="Foreground hierarchy">
          <div className="space-y-2">
            <p className="text-body text-ink-950">ink-950 — headings</p>
            <p className="text-body text-ink-900">ink-900 — emphasised body</p>
            <p className="text-body text-ink-600">ink-600 — body copy, 7.5:1 on white</p>
            <p className="text-body text-ink-500">ink-500 — muted; the floor for text</p>
            <p className="text-body text-ink-400">ink-400 — labels and glyphs only, never a sentence</p>
          </div>
        </Row>

        <Rule />

        <Row title="Buttons">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Start free</Button>
            <Button variant="secondary">See widgets</Button>
            <Button variant="ghost">Learn more</Button>
            <Button size="sm">Small</Button>
            <Button size="lg">Large</Button>
            <Button disabled>Disabled</Button>
          </div>

          <div className="on-ink mt-4 rounded-xl bg-ink-950 p-6">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="inverse">Start free</Button>
              <Button variant="inverse-ghost">Talk to us</Button>
            </div>
          </div>
        </Row>

        <Rule />

        <Row title="Links">
          <div className="space-y-3">
            <Text>
              An inline link is <TextLink to="/">underlined and accent-coloured</TextLink>,
              because colour alone is not a signal everybody receives.
            </Text>
            <ButtonLink to="/" variant="secondary" size="sm">
              Button-shaped link
            </ButtonLink>
          </div>
        </Row>

        <Rule />

        <Row title="Surfaces">
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="p-5">
              <p className="text-body-sm font-medium text-ink-950">Default</p>
              <p className="mt-1 text-caption text-ink-500">Hairline on white.</p>
            </Card>
            <Card tone="raised" className="p-5">
              <p className="text-body-sm font-medium text-ink-950">Raised</p>
              <p className="mt-1 text-caption text-ink-500">One shadow step.</p>
            </Card>
            <Card tone="sunken" className="p-5">
              <p className="text-body-sm font-medium text-ink-950">Sunken</p>
              <p className="mt-1 text-caption text-ink-500">A recess, not a lift.</p>
            </Card>
          </div>
        </Row>

        <Rule />

        <Row title="Badges">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Chrome extension</Badge>
            <Badge tone="positive">Native Wix app</Badge>
            <span className="on-ink inline-flex rounded-md bg-ink-950 p-2">
              <Badge tone="inverse">On ink</Badge>
            </span>
          </div>
        </Row>

        <Rule />

        <Row title="Ratings, avatars and sources">
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-6">
              <Stars rating={5} />
              <Stars rating={4.5} />
              <Stars rating={3} />
              <Stars rating={4.5} size={22} />
            </div>

            <div>
              <RatingSummary rating={4.8} total={312} />
            </div>
            <div>
              <RatingSummary rating={4.8} total={312} size="lg" />
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <Avatar name="Sarah Johnson" src="/images/sample/a1.svg" />
              <Avatar name="Michael Chen" />
              <AvatarRow
                people={[
                  { name: 'Sarah Johnson', avatar: '/images/sample/a1.svg' },
                  { name: 'Michael Chen', avatar: '/images/sample/a2.svg' },
                  { name: 'Amara Osei', avatar: '/images/sample/a3.svg' },
                  { name: 'Tom Baker' },
                ]}
              />
            </div>

            <div className="flex flex-wrap items-center gap-5">
              {CONNECT_SOURCES.map((source) => (
                <SourceTag key={source.id} source={source.id} />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {CONNECT_SOURCES.map((source) => (
                <SourceBadge key={source.id} source={source.id} />
              ))}
            </div>
          </div>
        </Row>

        <Rule />

        <Row title="Widget layout sketches">
          <p className="mb-4 text-caption text-ink-500">
            The dashboard's own thumbnails, from the shared package. Full review
            cards are drawn by the real widget renderer, never by this system.
          </p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            {WIDGET_TYPES.map((type) => (
              <div key={type}>
                <WidgetThumbnail type={type} />
                <p className="mt-2 text-micro text-ink-500 capitalize">{type}</p>
              </div>
            ))}
          </div>
        </Row>

        <Rule />

        <Row title="Form controls">
          <div className="grid max-w-md gap-5">
            <Field label="Your Google Business Profile URL" placeholder="https://maps.google.com/?cid=…" />
            <Field
              label="With a hint"
              placeholder="https://…"
              hint="Paste the address of your public listing."
            />
            <Field
              label="With an error"
              defaultValue="not-a-url"
              error="That doesn’t look like a full web address."
            />
            <SelectField label="Widget layout" defaultValue="masonry">
              <option value="carousel">Carousel</option>
              <option value="masonry">Masonry</option>
              <option value="grid">Grid</option>
            </SelectField>
          </div>
        </Row>

        <Rule />

        <Row title="Pull quote">
          <Quote cite="Not a real customer — a specimen.">
            The reviews were already there. Getting them onto the page took an
            afternoon.
          </Quote>
        </Row>
      </Container>
    </>
  );
}
